/* jr-consent.js — "Your Privacy Choices" on jeremysrazors.com.
 *
 * Replaces the OneTrust banner, whose published config went 404 and left the
 * storefront with no way for a visitor to change or even see their privacy
 * choices (the footer's "CookieSettings" button was OneTrust's and died with
 * it).
 *
 * WHO ENFORCES WHAT. Shopify's Customer Privacy API is the single source of
 * truth for consent on this store, and Shopify's pixel manager is what
 * actually blocks app and custom pixels (Meta, Google, Klaviyo, our own Web
 * Pixel) — in the regions the store's privacy settings mark as opt-in (cookie
 * banner regions) or opt-out (data-sharing opt-out states). This file never
 * reads or writes a Shopify cookie; it only calls the API. What it adds:
 *
 *   1. The control. A preferences dialog, opened from the footer link on every
 *      page, that writes the visitor's choice through setTrackingConsent().
 *      Accept, Reject and Save are the same size and weight; closing the
 *      dialog changes nothing and records nothing.
 *   2. Global Privacy Control. When the browser sends GPC we turn marketing
 *      off through the API (Shopify turns sale_of_data off on its own in
 *      opt-out regions), say so on the page once, and lock those two switches
 *      in the dialog with the reason.
 *   3. The receipt. Every change — ours, Shopify's own banner in opt-in
 *      regions, or GPC — is POSTed to the consent-receipt edge function, which
 *      is the audit trail. The subject id is minted here for consent only and
 *      is deliberately not the analytics id.
 *   4. A gate for tags the THEME loads itself, which the pixel manager cannot
 *      see: window.jrConsentQ.push(['marketing', fn]) runs fn once, and only
 *      when that purpose (and data sharing) is allowed.
 *
 * Fails SAFE: if the privacy API never loads, queued marketing tags never run
 * and the dialog says it could not load rather than pretending to save.
 */
(function () {
  'use strict';
  if (window.jrConsent) return;

  var cfgEl = document.getElementById('jr-consent-config');
  var CFG = {};
  try { CFG = JSON.parse(cfgEl ? cfgEl.textContent : '{}') || {}; } catch (_) { CFG = {}; }

  var SUBJECT_KEY = 'jr_consent_subject_v1';
  var GPC_NOTICE_KEY = 'jr_consent_gpc_notice_v1';
  var LAST_SENT_KEY = 'jr_consent_last_receipt_v1';
  var BANNER_VERSION = 'jr-consent-1';
  var PURPOSES = ['analytics', 'marketing', 'preferences', 'sale_of_data'];

  var cp = null;            // window.Shopify.customerPrivacy once loaded
  var pendingSource = null; // set by our own UI just before it writes consent
  var ran = [];             // queue entries already executed

  function gpcOn() {
    try { return navigator.globalPrivacyControl === true; } catch (_) { return false; }
  }

  function store(key, val) {
    try {
      if (val === undefined) return window.localStorage.getItem(key);
      window.localStorage.setItem(key, val);
    } catch (_) { /* private mode */ }
    return null;
  }

  function subjectId() {
    var v = store(SUBJECT_KEY);
    if (v && /^[A-Za-z0-9_-]{16,64}$/.test(v)) return v;
    var bytes = new Uint8Array(18);
    try { window.crypto.getRandomValues(bytes); } catch (_) {
      for (var i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
    }
    v = 'c_' + Array.prototype.map.call(bytes, function (b) { return ('0' + b.toString(16)).slice(-2); }).join('');
    store(SUBJECT_KEY, v);
    return v;
  }

  // What is ALLOWED right now — the API folds in region, store settings, GPC
  // and the visitor's own choice. This, not currentVisitorConsent(), is what
  // the pixel manager acts on, so it is what the receipt records.
  function allowed() {
    if (!cp) return null;
    return {
      analytics: cp.analyticsProcessingAllowed() === true,
      marketing: cp.marketingAllowed() === true,
      preferences: cp.preferencesProcessingAllowed() === true,
      sale_of_data: cp.saleOfDataAllowed() === true,
    };
  }

  function region() {
    try {
      var r = cp && cp.getRegion ? cp.getRegion() : '';
      return r && /^[A-Za-z]{2}[A-Za-z0-9]{0,3}$/.test(r) ? r : 'other';
    } catch (_) { return 'other'; }
  }

  // ── receipts ───────────────────────────────────────────────────────────
  function sendReceipt(source) {
    var choices = allowed();
    if (!choices || !CFG.endpoint) return;
    var body = {
      brand: 'jr',
      subject_id: subjectId(),
      source: source,
      region: region(),
      policy_version: CFG.policy_version || 'unversioned',
      banner_version: BANNER_VERSION,
      choices: choices,
      gpc: gpcOn(),
      decided_at: new Date().toISOString(),
      page_path: window.location.pathname,
    };
    // Same state, same source: nothing was decided, so nothing is recorded.
    var sig = source + '|' + PURPOSES.map(function (k) { return choices[k] ? 1 : 0; }).join('') + '|' + (body.gpc ? 1 : 0);
    if (store(LAST_SENT_KEY) === sig) return;
    try {
      fetch(CFG.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' }, // no preflight; the function parses the text
        body: JSON.stringify(body),
        keepalive: true,
        credentials: 'omit',
      }).then(function (res) {
        if (res && res.ok) store(LAST_SENT_KEY, sig);
      }).catch(function () { /* retried on the next page load: sig was not stored */ });
    } catch (_) { /* fetch unavailable */ }
  }

  function inferSource(c) {
    if (c.analytics && c.marketing) return 'banner_accept';
    if (!c.analytics && !c.marketing) return 'banner_reject';
    return 'preferences';
  }

  // ── theme-loaded tags ──────────────────────────────────────────────────
  function drainQueue() {
    var a = allowed();
    if (!a) return;
    var q = window.jrConsentQ || [];
    for (var i = 0; i < q.length; i++) {
      var item = q[i];
      if (!item || ran.indexOf(item) !== -1) continue;
      var purpose = item[0];
      // Anything that hands data to an ad or measurement vendor is "sharing".
      var ok = purpose === 'marketing' ? (a.marketing && a.sale_of_data) : a[purpose] === true;
      if (!ok) continue;
      ran.push(item);
      try { item[1](); } catch (_) { /* a vendor tag must not break the page */ }
    }
  }

  // ── writing a choice ───────────────────────────────────────────────────
  function write(choices, source, done) {
    if (!cp) { if (done) done(false); return; }
    var payload = {
      analytics: !!choices.analytics,
      marketing: !!choices.marketing,
      preferences: !!choices.preferences,
      sale_of_data: !!choices.sale_of_data,
    };
    if (gpcOn()) { payload.marketing = false; payload.sale_of_data = false; }
    pendingSource = source;
    cp.setTrackingConsent(payload, function () {
      // visitorConsentCollected only fires on a CHANGE; send here too so a
      // re-confirmed choice is still answered (sendReceipt de-duplicates).
      sendReceipt(source);
      pendingSource = null;
      drainQueue();
      if (done) done(true);
    });
  }

  function honorGpc() {
    if (!gpcOn() || !cp) return;
    var a = allowed();
    // Both, everywhere: Shopify switches sale_of_data off by itself only in the
    // states configured for opt-out, and a receipt must never claim an opt-out
    // the store is not actually applying.
    if (a && (a.marketing || a.sale_of_data)) {
      pendingSource = 'gpc';
      cp.setTrackingConsent({ marketing: false, sale_of_data: false }, function () {
        sendReceipt('gpc');
        pendingSource = null;
      });
    } else {
      sendReceipt('gpc');
    }
    if (!store(GPC_NOTICE_KEY)) {
      store(GPC_NOTICE_KEY, '1');
      toast('Opt-out preference signal honored. Your browser’s Global Privacy Control is on, so we won’t sell or share your personal information or use it for targeted advertising.');
    }
  }

  // ── UI ─────────────────────────────────────────────────────────────────
  var root = null;

  function el(tag, attrs, text) {
    var n = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) { n.setAttribute(k, attrs[k]); });
    if (text != null) n.textContent = text;
    return n;
  }

  function toast(msg) {
    var t = el('div', { class: 'jr-consent jr-consent__toast', role: 'status' }, msg);
    document.body.appendChild(t);
    setTimeout(function () { if (t.parentNode) t.parentNode.removeChild(t); }, 9000);
  }

  var ROWS = [
    ['analytics', 'Analytics', 'Helps us see which pages work and where the store gets in your way.'],
    ['marketing', 'Marketing and advertising', 'Lets ad platforms measure our ads and show you ones based on your visit.'],
    ['preferences', 'Preferences', 'Remembers choices such as your country and currency.'],
    ['sale_of_data', 'Sale or sharing of my personal information', 'Turn this off to opt out of the sale or sharing of your personal information, including for targeted advertising.'],
  ];

  function close() {
    if (!root) return;
    document.removeEventListener('keydown', onKey);
    if (root.parentNode) root.parentNode.removeChild(root);
    root = null;
    if (close.returnTo && close.returnTo.focus) close.returnTo.focus();
  }

  function onKey(e) {
    if (e.key === 'Escape') { close(); return; }
    if (e.key !== 'Tab' || !root) return;
    var f = root.querySelectorAll('button, input, a[href]');
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function open(trigger) {
    if (root) return;
    close.returnTo = trigger || document.activeElement;
    var a = allowed();
    var gpc = gpcOn();

    root = el('div', { class: 'jr-consent jr-consent__overlay' });
    var panel = el('div', { class: 'jr-consent__panel', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'jr-consent-title' });
    root.appendChild(panel);
    root.addEventListener('click', function (e) { if (e.target === root) close(); });

    var x = el('button', { type: 'button', class: 'jr-consent__close', 'aria-label': 'Close without changing anything' }, '×');
    x.addEventListener('click', close);
    panel.appendChild(x);
    panel.appendChild(el('h2', { id: 'jr-consent-title', class: 'jr-consent__title' }, 'Your Privacy Choices'));

    if (!a) {
      panel.appendChild(el('p', { class: 'jr-consent__lede' }, 'We couldn’t load your privacy settings just now, so nothing has been changed. Please reload the page and try again.'));
      document.body.appendChild(root);
      document.addEventListener('keydown', onKey);
      x.focus();
      return;
    }

    panel.appendChild(el('p', { class: 'jr-consent__lede' },
      'Choose what we and our partners may do with information about your visit. Your order, your account and the cart work the same whatever you pick.'));
    if (gpc) {
      panel.appendChild(el('p', { class: 'jr-consent__gpc', role: 'status' },
        'Opt-out preference signal honored. Your browser’s Global Privacy Control is on, so marketing and the sale or sharing of your information stay off.'));
    }

    var inputs = {};
    ROWS.forEach(function (r) {
      var key = r[0];
      var locked = gpc && (key === 'marketing' || key === 'sale_of_data');
      var row = el('label', { class: 'jr-consent__row' + (locked ? ' is-locked' : '') });
      var box = el('input', { type: 'checkbox', class: 'jr-consent__check' });
      box.checked = locked ? false : !!a[key];
      if (locked) box.disabled = true;
      inputs[key] = box;
      var txt = el('span', { class: 'jr-consent__text' });
      txt.appendChild(el('span', { class: 'jr-consent__name' }, r[1]));
      txt.appendChild(el('span', { class: 'jr-consent__desc' }, r[2]));
      row.appendChild(box);
      row.appendChild(txt);
      panel.appendChild(row);
    });

    var status = el('p', { class: 'jr-consent__status', role: 'status', 'aria-live': 'polite' });
    var actions = el('div', { class: 'jr-consent__actions' });

    function commit(choices, source, label) {
      status.textContent = 'Saving…';
      write(choices, source, function (ok) {
        if (!ok) { status.textContent = 'That didn’t save. Please reload the page and try again.'; return; }
        status.textContent = label;
        setTimeout(close, 1100);
      });
    }

    // Three buttons, one style: rejecting takes exactly as many clicks as accepting.
    var reject = el('button', { type: 'button', class: 'jr-consent__btn' }, 'Reject all');
    reject.addEventListener('click', function () {
      commit({ analytics: false, marketing: false, preferences: false, sale_of_data: false }, 'banner_reject', 'Saved. Everything optional is off.');
    });
    var save = el('button', { type: 'button', class: 'jr-consent__btn' }, 'Save my choices');
    save.addEventListener('click', function () {
      var c = {};
      PURPOSES.forEach(function (k) { c[k] = inputs[k].checked; });
      var source = (!c.sale_of_data && a.sale_of_data) ? 'do_not_sell_link' : 'preferences';
      commit(c, source, 'Saved.');
    });
    var accept = el('button', { type: 'button', class: 'jr-consent__btn' }, 'Accept all');
    accept.addEventListener('click', function () {
      commit({ analytics: true, marketing: true, preferences: true, sale_of_data: true }, 'banner_accept',
        gpc ? 'Saved. Marketing and sharing stay off because of your browser’s signal.' : 'Saved.');
    });
    actions.appendChild(reject);
    actions.appendChild(save);
    actions.appendChild(accept);
    panel.appendChild(actions);
    panel.appendChild(status);

    if (CFG.policy_url) {
      var foot = el('p', { class: 'jr-consent__foot' });
      foot.appendChild(el('a', { href: CFG.policy_url }, 'Read our privacy policy'));
      panel.appendChild(foot);
    }

    document.body.appendChild(root);
    document.addEventListener('keydown', onKey);
    x.focus();
  }

  // ── boot ───────────────────────────────────────────────────────────────
  window.jrConsent = { open: open, allowed: allowed };

  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target.closest('[data-jr-consent-open]') : null;
    if (!t) return;
    e.preventDefault();
    open(t);
  });

  document.addEventListener('visitorConsentCollected', function () {
    // Shopify's own banner (opt-in regions) and our dialog both land here.
    if (!pendingSource) { var a = allowed(); if (a) sendReceipt(inferSource(a)); }
    drainQueue();
  });

  function ready() {
    cp = window.Shopify && window.Shopify.customerPrivacy ? window.Shopify.customerPrivacy : null;
    if (!cp) return;
    honorGpc();
    drainQueue();
    if (window.location.hash === '#privacy-choices') open(null);
  }

  if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (err) {
      if (!err) ready();
    });
  }
})();
